"use client";

import { ReactNode, useMemo } from "react";

import { Badge } from "@/components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn, formatIsoStringDate } from "@/lib/utils";
import { t } from "@/lib/localization";

import { GetFilesResponseType } from "../../hooks/useGetFiles";
import { useGetLomdaV2 } from "@/features/storage/sheets/lomdaV2Sheet/hooks/useGetLomdaV2";

import {
    LomdaContent,
    LomdaMetadata,
} from "@/features/lomdaEditorV2/lib/lomdaTypes";
import LomdaV2Thumbnail from "@/features/lomdaEditorV2/utils/lomdaV2Thumbnail";

interface LomdaV2ListItemProps {
    file: GetFilesResponseType["data"][0];
    onClick?: () => void;
    options?: ReactNode;
    selected?: boolean;
}

export default function LomdaV2ListItem({
    file,
    onClick,
    options,
    selected,
}: LomdaV2ListItemProps) {
    const isMobile = useIsMobile();

    const { data: lomdaData } = useGetLomdaV2(true, file.id);
    const lomda = lomdaData?.data;

    const content: LomdaContent | null = lomda?.content
        ? JSON.parse(lomda.content)
        : null;
    const metadata: LomdaMetadata | null = lomda?.metadata
        ? JSON.parse(lomda.metadata)
        : null;

    const { thumbnailWidth, titleMinWidth, optionsWidth } = useMemo(
        () => ({
            thumbnailWidth: isMobile ? 150 : 180,
            titleMinWidth: isMobile ? 140 : 200,
            optionsWidth: 50,
        }),
        [isMobile]
    );

    const badgeText =
        file.detailForBadge <= 1
            ? t(
                  "storage.lomda-card.num-of-scenes-badge.single",
                  undefined,
                  undefined
              )
            : t(
                  "storage.lomda-card.num-of-scenes-badge.multiple",
                  undefined,
                  undefined
              );

    const title = file.name || t("storage.no-name", undefined, undefined);
    const subtitle = `${t("storage.lomda-v2", undefined, undefined)} · ${formatIsoStringDate(file.date)}`;

    const gridCols = `${thumbnailWidth}px 1fr ${optionsWidth}px`;

    return (
        <div
            onClick={onClick}
            className={cn(
                "group cursor-pointer bg-card hover:bg-accent transition",
                "grid items-center w-full p-4 gap-2 sm:gap-4 overflow-x-auto",
                selected && "border-s-4 border-s-primary"
            )}
            style={{ gridTemplateColumns: gridCols }}
        >
            <div
                className="aspect-video rounded-md relative flex-shrink-0 border overflow-hidden bg-muted"
                style={{ width: thumbnailWidth }}
            >
                <LomdaV2Thumbnail
                    content={content}
                    metadata={metadata}
                    lomdaId={file.id}
                    lomdaName={file.name || ""}
                    userId={lomda?.userId ?? ""}
                />

                <Badge className="absolute bottom-2 left-2 font-normal px-2">
                    {badgeText}
                </Badge>
            </div>

            <div
                className="flex items-center gap-3"
                style={{ minWidth: titleMinWidth }}
            >
                <div className="flex flex-col gap-0.5 truncate">
                    <h3
                        className="text-base font-medium truncate"
                        title={title}
                    >
                        {title}
                    </h3>
                    <p className="text-sm text-muted-foreground truncate">
                        {subtitle}
                    </p>
                </div>
            </div>

            <div className="justify-self-end flex-shrink-0">{options}</div>
        </div>
    );
}
